import { InputHTMLAttributes, forwardRef, ReactNode, useId } from 'react';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, description, error, className, disabled, id: idProp, checked, ...rest },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const boxBase =
    'w-4 h-4 shrink-0 rounded-full border transition-colors duration-fast ease-smooth flex items-center justify-center';
  const boxState = error
    ? 'border-danger/60 bg-surface-base'
    : checked
      ? 'border-accent-primary bg-surface-base'
      : 'border-border bg-surface-base hover:border-accent-primary/50';
  const wrapperDisabled = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

  return (
    <label
      htmlFor={id}
      className={['inline-flex items-start gap-2.5', wrapperDisabled, className].filter(Boolean).join(' ')}
    >
      <span className="relative inline-flex items-center">
        <input
          ref={ref}
          type="radio"
          id={id}
          checked={checked}
          disabled={disabled}
          className="peer sr-only"
          {...rest}
        />
        <span
          className={[
            boxBase,
            boxState,
            'peer-focus-visible:ring-2 peer-focus-visible:ring-accent-primary/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-base',
          ].join(' ')}
          aria-hidden="true"
        >
          {checked && <span className="w-2 h-2 rounded-full bg-accent-primary" />}
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col min-w-0">
          {label && <span className="text-sm text-text-primary leading-tight select-none">{label}</span>}
          {description && <span className="text-xs text-text-muted leading-snug mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});

export interface RadioGroupOption<T extends string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string> {
  name: string;
  value?: T;
  onChange?: (value: T) => void;
  options: RadioGroupOption<T>[];
  disabled?: boolean;
  error?: boolean;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export function RadioGroup<T extends string>({
  name,
  value,
  onChange,
  options,
  disabled,
  error,
  orientation = 'vertical',
  className,
}: RadioGroupProps<T>) {
  return (
    <div
      role="radiogroup"
      className={[
        orientation === 'vertical' ? 'flex flex-col gap-2.5' : 'flex flex-wrap gap-4',
        className,
      ].filter(Boolean).join(' ')}
    >
      {options.map((opt) => (
        <Radio
          key={opt.value}
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange?.(opt.value)}
          disabled={disabled || opt.disabled}
          error={error}
          label={opt.label}
          description={opt.description}
        />
      ))}
    </div>
  );
}
